import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  parseStack,
  serializeStack,
  TROWSER_PARAM,
  TROWSER_SECTION_PARAM,
  TrowserKey,
} from '@/modules/navigation/trowsers/trowser.types';

import {
  LEGACY_LINK_DATA_INITIAL_STEP_PARAM,
  LEGACY_LINK_DATA_MODAL_PARAM,
} from '../types';

/**
 * Rewrites a pre-trowser Link-data link to today's params, in place.
 *
 * `linkDataModal=true` becomes the trowser's `modalOpen` entry and
 * `linkDataInitialStep` becomes the trowser's `section`, so a
 * bookmarked or shared link still opens the wizard on the step it named. Both
 * legacy params are dropped once translated, leaving a canonical URL.
 *
 * Replaces rather than pushes: the user didn't navigate, so this shouldn't add an
 * entry to Back through. The open is therefore not flagged as pushed, which is
 * correct — closing a deep-linked open strips the params instead of popping an
 * entry that was never pushed.
 */
export function useMigrateLegacyLinkDataParams(): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const legacyOpen = searchParams.get(LEGACY_LINK_DATA_MODAL_PARAM);
  const legacyStep = searchParams.get(LEGACY_LINK_DATA_INITIAL_STEP_PARAM);

  useEffect(() => {
    if (!legacyOpen && !legacyStep) return;

    setSearchParams(
      (prev) => {
        if (legacyOpen === 'true') {
          // Add to the stack rather than overwrite it: another trowser may
          // already be open in this URL.
          const stack = parseStack(prev.get(TROWSER_PARAM));
          const isAlreadyOpen = stack.some(
            (trowser) => trowser.key === TrowserKey.LinkHouseholdData
          );
          if (!isAlreadyOpen) {
            stack.push({ key: TrowserKey.LinkHouseholdData });
          }
          prev.set(TROWSER_PARAM, serializeStack(stack));
        }

        if (legacyStep) {
          prev.set(TROWSER_SECTION_PARAM, legacyStep);
        }

        prev.delete(LEGACY_LINK_DATA_MODAL_PARAM);
        prev.delete(LEGACY_LINK_DATA_INITIAL_STEP_PARAM);
        return prev;
      },
      { replace: true, state: location.state }
    );
  }, [legacyOpen, legacyStep, setSearchParams, location.state]);
}
